import { LiteratureReviewPlan } from "@core/domain/models/literature-review-plan.model";
import { BuiltInImportSourceId } from "@core/domain/types/builtInImportSourceId";
import { LevelOfEvidence } from "@core/domain/types/levelOfEvidence.type";

export function generateProcedureSearchQuery(importSourceId: string, plan: LiteratureReviewPlan) {
  if (importSourceId === BuiltInImportSourceId.PUBMED) {
    return generatePubmedProcedureSearchQuery(plan);
  }
  if (importSourceId === BuiltInImportSourceId.EUROPE_PMC) {
    return generateEuropePmcProcedureSearchQuery(plan);
  }
  return "";
}

function generatePubmedProcedureSearchQuery(plan: LiteratureReviewPlan) {
  const loe = plan?.targetLoe;
  const procedureNames = [...(plan?.alternativeProcedureNames ?? []), plan?.procedureName];

  const storedFrom = plan?.inclusionCriteria?.timeLimitFromDate;
  const storedTo = plan?.inclusionCriteria?.timeLimitToDate;

  const formatDatePubmed = (isoDate: string) => isoDate.replaceAll("-", "/");

  const formattedStartDate = storedFrom
    ? formatDatePubmed(storedFrom)
    : (() => {
        const d = new Date();
        d.setFullYear(d.getFullYear() - (plan?.timeRange ?? 5));
        return `${d.getFullYear()}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getDate().toString().padStart(2, "0")}`;
      })();

  const formattedEndDate = storedTo
    ? formatDatePubmed(storedTo)
    : (() => {
        const d = new Date();
        return `${d.getFullYear()}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getDate().toString().padStart(2, "0")}`;
      })();

  const timeRangeQuery = `(${formattedStartDate}:${formattedEndDate}[pdat])`;

  const procedureNamesQuery = `(${procedureNames
    .filter((name) => !!name)
    .map((name) => `"${name}"[Title]`)
    .join(" OR ")})`;

  switch (loe) {
    case LevelOfEvidence.A:
      return `${procedureNamesQuery} AND ("guideline"[Title] OR "Practice Guideline"[Publication Type] OR "Clinical Protocols"[MeSH] OR "Evidence-Based Practice"[MeSH]) AND ${timeRangeQuery}`;
    case LevelOfEvidence.B:
      return `${procedureNamesQuery} AND ("systematic review"[Title] OR "Review Literature as Topic"[MeSH] OR "meta-analysis"[Title] OR "Meta-Analysis"[Publication Type]) AND ${timeRangeQuery}`;
    case LevelOfEvidence.C:
      return `${procedureNamesQuery} AND ("randomized controlled trial"[Publication Type] OR "Randomized Controlled Trials as Topic"[MeSH] OR "random allocation" [MeSH] ) AND ${timeRangeQuery}`;
    case LevelOfEvidence.D:
      return `${procedureNamesQuery} AND ("randomized controlled trial"[Publication Type] OR "cohort studies" [MeSH] OR "Observational Studies as Topic"[MeSH] OR "Case-Control Studies"[MeSH] ) AND ${timeRangeQuery}`;
    case LevelOfEvidence.E:
      return `${procedureNamesQuery} AND (Case Reports[Publication Type]) AND ${timeRangeQuery}`;
    case LevelOfEvidence.F:
      return `${procedureNamesQuery} AND (Review[Publication Type]) AND ${timeRangeQuery}`;
  }
  return "";
}

function generateEuropePmcProcedureSearchQuery(plan: LiteratureReviewPlan) {
  const loe = plan?.targetLoe;
  const procedureNames = [...(plan?.alternativeProcedureNames ?? []), plan?.procedureName];

  const storedFrom = plan?.inclusionCriteria?.timeLimitFromDate;
  const storedTo = plan?.inclusionCriteria?.timeLimitToDate;

  const formattedStartDate = storedFrom
    ? storedFrom
    : (() => {
        const d = new Date();
        d.setFullYear(d.getFullYear() - (plan?.timeRange ?? 5));
        return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
      })();

  const formattedEndDate = storedTo
    ? storedTo
    : (() => {
        const d = new Date();
        return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
      })();

  const timeRangeQuery = `(FIRST_PDATE:[${formattedStartDate} TO ${formattedEndDate}])`;

  const procedureNamesQuery = `(${procedureNames
    .filter((name) => !!name)
    .map((name) => `TITLE:"${name}"`)
    .join(" OR ")})`;

  const pubTypeMap: Partial<Record<LevelOfEvidence, string>> = {
    [LevelOfEvidence.A]: `PUB_TYPE:("guideline")`,
    [LevelOfEvidence.B]: `(PUB_TYPE:("meta analysis") OR PUB_TYPE:("systematic review"))`,
    [LevelOfEvidence.C]: `PUB_TYPE:("randomized controlled trial")`,
    [LevelOfEvidence.D]: `PUB_TYPE:("observational study")`,
    [LevelOfEvidence.E]: `PUB_TYPE:("case reports")`,
    [LevelOfEvidence.F]: `PUB_TYPE:("review")`,
  };

  const pubTypeFilter = pubTypeMap[loe as LevelOfEvidence];
  if (!pubTypeFilter) return "";

  return `${procedureNamesQuery} AND ${pubTypeFilter} AND ${timeRangeQuery}`;
}
